import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "magicSheepSparklePost"
const TRAIL_SPACING_PX = 24
const TRAIL_DURATION_MS = 900
const TRAIL_POOL_LIMIT = 24
const SVG_NS = "http://www.w3.org/2000/svg"

export default class extends Controller {
  static targets = [ "idea", "notes", "form", "name", "sheep", "change", "why", "status" ]
  static values = { initialNotes: Array }

  connect() {
    this.trailIndex = 0
    this.confirmedNotes = this.initialNotesValue || []
    this.lastPointerPoint = null
    this.lastTrailAngle = 0
    this.accumulatedTrailDistance = 0
    this.trailPool = []
    this.activeTrailMarks = new Set()
    this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.prefersReducedMotion = this.reducedMotionQuery.matches
    this.boundTrail = this.trail.bind(this)
    this.boundReducedMotionChange = this.reducedMotionChange.bind(this)
    this.element.addEventListener("pointermove", this.boundTrail)
    this.reducedMotionQuery.addEventListener("change", this.boundReducedMotionChange)
    this.renderNotes()
    this.syncStoredNotes()
  }

  disconnect() {
    this.element.removeEventListener("pointermove", this.boundTrail)
    this.reducedMotionQuery.removeEventListener("change", this.boundReducedMotionChange)
    Array.from(this.activeTrailMarks).forEach((mark) => this.releaseHoofMark(mark))
    this.trailPool = []
  }

  reducedMotionChange(event) {
    this.prefersReducedMotion = event.matches
    if (this.prefersReducedMotion) {
      Array.from(this.activeTrailMarks).forEach((mark) => this.releaseHoofMark(mark))
    }
  }

  suggest(event) {
    const button = event.currentTarget
    const sheep = button.dataset.sheep || "the sheep"
    const idea = button.dataset.idea || button.textContent.trim()
    const sentence = `${sheep}: ${idea}. I think this would help because...`

    if (this.hasIdeaTarget) {
      this.ideaTarget.value = sentence
      this.ideaTarget.focus()
    }

    button.closest(".ms-help-character")?.classList.add("ms-help-character--sparked")
    window.setTimeout(() => button.closest(".ms-help-character")?.classList.remove("ms-help-character--sparked"), 900)
  }

  async submit(event) {
    event.preventDefault()

    const note = {
      name: this.valueFor("name") || "A trusted helper",
      sheep: this.valueFor("sheep") || "the Magic Sheep",
      change: this.valueFor("change") || "A helpful idea",
      why: this.valueFor("why") || "It would make the website more magical",
      createdAt: new Date().toISOString()
    }

    this.setStatus("Ada is folding your note into a tiny woolly envelope...")

    try {
      const savedNote = await this.save(note)
      this.addConfirmedNote(savedNote)
      localStorage.removeItem(STORAGE_KEY)
      this.formTarget.reset()
      this.renderNotes()
      this.notesTarget.closest(".ms-help-notes")?.classList.add("ms-help-notes--sent")
      this.setStatus("Your note has been sent by Sparkle Post. Ada is checking it carefully with one hoof and one emergency pencil.")
    } catch {
      this.setStatus("Ada could not send that Sparkle Post yet. Please check the boxes and try again.")
    }
  }

  async save(note) {
    const response = await fetch(this.formTarget.action, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      credentials: "same-origin",
      body: this.formDataFor(note)
    })

    if (!response.ok) throw new Error("Sparkle Post could not be saved")

    const data = await response.json()
    return data.suggestion || note
  }

  async syncStoredNotes() {
    if (!this.hasFormTarget) return

    const notes = this.notes()
    const unsyncedNotes = notes.filter((note) => note.synced !== true)
    if (unsyncedNotes.length === 0) return

    let changed = false
    const unsyncedNotesToKeep = []

    for (const note of notes) {
      if (note.synced === true) {
        changed = true
        continue
      }

      try {
        const savedNote = await this.save(this.normalizeNote(note))
        this.addConfirmedNote(savedNote)
        changed = true
      } catch {
        unsyncedNotesToKeep.push(note)
      }
    }

    if (changed) {
      if (unsyncedNotesToKeep.length === 0) {
        localStorage.removeItem(STORAGE_KEY)
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(unsyncedNotesToKeep))
      }
      this.renderNotes()
    }
  }

  addConfirmedNote(note) {
    const normalized = { ...this.normalizeNote(note), synced: true, id: note.id }
    const withoutDuplicate = this.confirmedNotes.filter((confirmedNote) => {
      if (normalized.id && confirmedNote.id) return confirmedNote.id !== normalized.id

      return !(
        confirmedNote.name === normalized.name &&
        confirmedNote.sheep === normalized.sheep &&
        confirmedNote.change === normalized.change &&
        confirmedNote.why === normalized.why
      )
    })

    this.confirmedNotes = [normalized, ...withoutDuplicate].slice(0, 4)
  }

  formDataFor(note) {
    const formData = new FormData(this.formTarget)
    formData.set("help_sheep_suggestion[helper_name]", note.name)
    formData.set("help_sheep_suggestion[subject]", note.sheep)
    formData.set("help_sheep_suggestion[suggestion]", note.change)
    formData.set("help_sheep_suggestion[reason]", note.why)
    return formData
  }

  normalizeNote(note) {
    return {
      name: note.name || "A trusted helper",
      sheep: note.sheep || "the Magic Sheep",
      change: note.change || "A helpful idea",
      why: note.why || "It would make the website more magical",
      createdAt: note.createdAt || new Date().toISOString()
    }
  }

  trail(event) {
    if (event.pointerType === "touch" || !this.trailAllowed()) return

    const point = { x: event.clientX, y: event.clientY }
    const previousPoint = this.lastPointerPoint
    this.lastPointerPoint = point

    const dx = previousPoint ? point.x - previousPoint.x : 0
    const dy = previousPoint ? point.y - previousPoint.y : 0
    const distance = Math.hypot(dx, dy)
    this.accumulatedTrailDistance += distance

    if (!previousPoint || distance <= 0.5 || this.accumulatedTrailDistance < TRAIL_SPACING_PX) return
    this.accumulatedTrailDistance = this.accumulatedTrailDistance % TRAIL_SPACING_PX

    const angle = distance > 0.5 ? Math.atan2(dy, dx) * 180 / Math.PI + 90 : this.lastTrailAngle
    const stepSide = this.trailIndex % 2 === 0 ? -1 : 1
    const sideOffset = 8 * stepSide
    const forwardX = distance > 0.5 ? dx / distance : 0
    const forwardY = distance > 0.5 ? dy / distance : 1
    const lateralX = -forwardY
    const lateralY = forwardX
    const markX = point.x + lateralX * sideOffset
    const markY = point.y + lateralY * sideOffset
    const driftX = forwardX * 14 + lateralX * sideOffset * 0.3
    const driftY = forwardY * 14 + lateralY * sideOffset * 0.3
    this.lastTrailAngle = angle

    const mark = this.takeHoofMark()
    mark.style.setProperty("--trail-x", `${markX.toFixed(2)}px`)
    mark.style.setProperty("--trail-y", `${markY.toFixed(2)}px`)
    mark.style.setProperty("--trail-rotate", `${angle}deg`)
    mark.style.setProperty("--trail-drift-x", `${driftX.toFixed(2)}px`)
    mark.style.setProperty("--trail-drift-y", `${driftY.toFixed(2)}px`)
    mark.style.setProperty("--trail-mid-drift-x", `${(driftX * 0.45).toFixed(2)}px`)
    mark.style.setProperty("--trail-mid-drift-y", `${(driftY * 0.45).toFixed(2)}px`)
    mark.style.setProperty("--trail-opacity-peak", this.randomTrailOpacity(0.42, 0.66).toFixed(2))
    mark.style.setProperty("--trail-opacity-middle", this.randomTrailOpacity(0.28, 0.5).toFixed(2))
    this.trailIndex += 1

    document.body.appendChild(mark)
    this.activeTrailMarks.add(mark)
    mark.trailTimer = window.setTimeout(() => this.releaseHoofMark(mark), TRAIL_DURATION_MS)
  }

  trailAllowed() {
    return !this.prefersReducedMotion
  }

  randomTrailOpacity(min, max) {
    return min + Math.random() * (max - min)
  }

  takeHoofMark() {
    return this.trailPool.pop() || this.createHoofMark()
  }

  releaseHoofMark(mark) {
    window.clearTimeout(mark.trailTimer)
    mark.trailTimer = null
    mark.remove()
    this.activeTrailMarks.delete(mark)
    if (this.trailPool.length < TRAIL_POOL_LIMIT) this.trailPool.push(mark)
  }

  createHoofMark() {
    const mark = document.createElement("span")
    mark.className = "ms-hoof-trail"
    mark.setAttribute("aria-hidden", "true")

    const svg = this.svgElement("svg", { class: "ms-hoof-trail__svg", viewBox: "0 0 48 64" })
    svg.append(
      this.svgElement("path", { class: "ms-hoof-trail__toe", d: "M19 5 C9 14 5 32 9 47 C11 56 16 61 21 59 C27 56 26 42 24 29 C23 18 22 9 19 5 Z" }),
      this.svgElement("path", { class: "ms-hoof-trail__toe", d: "M29 5 C39 14 43 32 39 47 C37 56 32 61 27 59 C21 56 22 42 24 29 C25 18 26 9 29 5 Z" })
    )
    mark.append(svg)
    return mark
  }

  svgElement(name, attributes) {
    const element = document.createElementNS(SVG_NS, name)
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value))
    return element
  }

  valueFor(name) {
    const target = this[`${name}Target`]
    return target?.value?.trim()
  }

  notes() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
    } catch {
      return []
    }
  }

  renderNotes() {
    if (!this.hasNotesTarget) return

    const notes = [
      ...this.confirmedNotes,
      ...this.notes().filter((note) => note.synced !== true)
    ].slice(0, 4)

    if (notes.length === 0) {
      this.notesTarget.innerHTML = "<p>No sparkle post yet. Ada is waiting beside the tiny woolly mailbox.</p>"
      return
    }

    this.notesTarget.innerHTML = notes.map((note) => `
      <article class="ms-sparkle-note">
        <strong>${this.escape(note.name)} helped ${this.escape(note.sheep)}</strong>
        <p>${this.escape(note.change)}</p>
        <small>${this.escape(note.why)}</small>
      </article>
    `).join("")
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
